import type { ReactNode } from 'react';

import {
  markdownEmphasisClasses,
  markdownInlineCodeClasses,
  markdownLinkClasses,
  markdownRefusedLinkClasses,
  markdownStrongClasses,
} from '@/modules/assistant/parts/styles';

/**
 * The local Markdown renderer — the INLINE half.
 *
 * ---------------------------------------------------------------------------
 * WHY NO HTML IS EVER EXECUTED
 * ---------------------------------------------------------------------------
 * Every node here is a React ELEMENT built from the source text. Nothing is ever
 * turned into markup, so a model's `<script>` is text — it is rendered, escaped,
 * exactly as a reader would see it in the source. There is no path in this file
 * that produces a raw HTML string.
 *
 * ---------------------------------------------------------------------------
 * WHY ONLY THREE LINK SCHEMES
 * ---------------------------------------------------------------------------
 * A link is the one place a model's output can cause a browser to DO something.
 * `javascript:` runs code in the page it was rendered in, and `data:` smuggles a
 * document into a frame. So a URL is a link only when its scheme is `http`,
 * `https` or `mailto`; anything else — including a relative URL and including a
 * scheme nobody has thought of — is shown as plain text, with the URL beside it
 * so the reader can see what the model actually wrote.
 */

/** The schemes the console is willing to turn into a link. */
const SAFE_LINK_SCHEMES = ['http:', 'https:', 'mailto:'];

/** A URL scheme at the start of a URL. */
const URL_SCHEME = /^[a-zA-Z][a-zA-Z0-9+.-]*:/;

/** A matched inline run, and the index just after it. */
type InlineMatch = { element: ReactNode; next: number };

/**
 * The href a URL may be rendered with, or null when it may not.
 *
 * A URL with no scheme at all is a relative reference, and the console does not
 * turn those into links: the assistant's answers are read in place, and a
 * relative link in a chat panel has nothing to point at.
 */
function safeLinkHref(url: string): string | null {
  const trimmed = url.trim();
  if (trimmed === '') return null;

  const scheme = (URL_SCHEME.exec(trimmed)?.[0] ?? '').toLowerCase();
  return SAFE_LINK_SCHEMES.includes(scheme) ? trimmed : null;
}

/**
 * Renders a run of inline Markdown into React nodes.
 *
 * The scan is left to right and the FIRST delimiter wins, which is what makes
 * `**` bold rather than two italics, and what makes an inline code span keep its
 * asterisks.
 */
export function renderInline(source: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let buffer = '';
  let index = 0;

  while (index < source.length) {
    const match = matchAt(source, index);

    if (match !== null) {
      if (buffer !== '') {
        nodes.push(buffer);
        buffer = '';
      }
      nodes.push(match.element);
      index = match.next;
      continue;
    }

    buffer += source[index] ?? '';
    index += 1;
  }

  if (buffer !== '') nodes.push(buffer);
  return nodes;
}

/** The first inline construct at `index`, whatever it is. */
function matchAt(source: string, index: number): InlineMatch | null {
  const char = source[index] ?? '';
  if (char === '`') return matchCode(source, index);
  if (char === '[') return matchLink(source, index);
  return matchEmphasis(source, index);
}

/** Matches an inline code span. */
function matchCode(source: string, index: number): InlineMatch | null {
  const end = source.indexOf('`', index + 1);
  if (end === -1) return null;

  return {
    element: (
      <code key={`code-${index}`} className={markdownInlineCodeClasses}>
        {source.slice(index + 1, end)}
      </code>
    ),
    next: end + 1,
  };
}

/** Matches `**bold**`, `*italic*`, `__bold__` and `_italic_`. */
function matchEmphasis(source: string, index: number): InlineMatch | null {
  const char = source[index] ?? '';
  if (char !== '*' && char !== '_') return null;
  if (!canOpenEmphasis(source, index, char)) return null;

  const doubled = source[index + 1] === char;
  const delimiter = doubled ? char + char : char;
  const contentStart = index + delimiter.length;
  const end = source.indexOf(delimiter, contentStart);
  if (end === -1 || end === contentStart) return null;

  const content = source.slice(contentStart, end);
  if (!isEmphasisContent(content)) return null;

  return { element: buildEmphasis(doubled, content, index), next: end + delimiter.length };
}

/**
 * True when the delimiter may open an emphasis run.
 *
 * An underscore inside a word is a word character: `order_id` stays as it is.
 */
function canOpenEmphasis(source: string, index: number, char: string): boolean {
  if (char !== '_') return true;
  if (index === 0) return true;
  return !isWordCharacter(source[index - 1] ?? '');
}

/**
 * True when the text between two delimiters may be emphasised.
 *
 * Emphasis cannot begin or end with whitespace, so `2 * 3 * 4` stays arithmetic
 * rather than becoming a stray italic.
 */
function isEmphasisContent(content: string): boolean {
  if (content.trim() === '') return false;
  return !/^\s|\s$/.test(content);
}

/** Builds the element for an emphasis run. */
function buildEmphasis(doubled: boolean, content: string, index: number): ReactNode {
  const children = renderInline(content);

  return doubled ? (
    <strong key={`strong-${index}`} className={markdownStrongClasses}>
      {children}
    </strong>
  ) : (
    <em key={`em-${index}`} className={markdownEmphasisClasses}>
      {children}
    </em>
  );
}

/** Matches `[label](url)`. */
function matchLink(source: string, index: number): InlineMatch | null {
  const labelEnd = source.indexOf(']', index + 1);
  if (labelEnd === -1) return null;
  if (source[labelEnd + 1] !== '(') return null;

  const urlEnd = source.indexOf(')', labelEnd + 2);
  if (urlEnd === -1) return null;

  const label = source.slice(index + 1, labelEnd);
  const url = source.slice(labelEnd + 2, urlEnd).trim();
  const href = safeLinkHref(url);
  const children = renderInline(label);

  const element =
    href === null ? (
      <span key={`link-${index}`}>
        {children}
        <span className={markdownRefusedLinkClasses}> ({url})</span>
      </span>
    ) : (
      <a
        key={`link-${index}`}
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={markdownLinkClasses}
      >
        {children}
      </a>
    );

  return { element, next: urlEnd + 1 };
}

/** True for a character that may be part of a word. */
function isWordCharacter(char: string): boolean {
  return /[A-Za-z0-9]/.test(char);
}
